// The browser-calling registration endpoint (TMF-346) — the one place the SPA
// asks to be rung, and the one place it asks to stop being rung.
//
// The API contract, session-scoped in both directions (no id crosses the wire —
// the bearer token names the rep):
//
//   POST   /api/auth/voice-token → 201 { identity, token, expires_at }
//   DELETE /api/auth/voice-token → 204
//
// The token is a vendor access token, not one of ours: it is handed straight to
// the Voice SDK and never stored, logged, or put in a URL. It lives for minutes,
// so a browser holds it in memory for exactly as long as it is registered.

import { halFetch } from "~/lib/hal";

/** The registration as the API returns it. `expires_at` is RFC 3339. */
export interface VoiceRegistration {
  identity: string;
  token: string;
  expires_at: string;
}

/**
 * Register this browser to receive inbound calls, and get the credential it
 * registers with. Called on mount and again on a fixed cadence
 * (useVoiceDevice's 30-second renewal interval, TMF-484): the window on the
 * server and the token in the browser are the same short-lived window, so
 * refreshing one refreshes the other.
 *
 * liveCall reports whether this browser holds a live or placing call right
 * now (TMF-382 review V4), the same predicate attach() rejects an offer on.
 * The server reads it to decide whether an idle renewal may heal a busy
 * marker a lost end-of-call callback left stuck.
 *
 * Throws HalError like every other halFetch caller; the hook that owns the
 * device is where that becomes an Offline indicator rather than a crash.
 */
export async function registerVoice(
  liveCall: boolean,
): Promise<VoiceRegistration> {
  return halFetch<VoiceRegistration>("/api/auth/voice-token", {
    method: "POST",
    body: { live_call: liveCall },
  });
}

/** Stop this browser being rung — signing out, or leaving the app. */
export async function unregisterVoice(): Promise<void> {
  await halFetch<null>("/api/auth/voice-token", { method: "DELETE" });
}
